/* eslint-disable no-shadow */
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { HttpError, PMSError, ValidationError } from '@/errors';
import Vue from 'vue';

const state = {
  onlinePresence: null,
  pending: false,
  updatePending: false,
  logs: [],
  requestIds: [],
  logsPending: false,
  queuePositionPending: false,
  queuePosition: [],
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  return err;
}

const getters = {
  onlinePresence: (state) => state.onlinePresence ?? [],
  pending: (state) => state.pending,
  updatePending: (state) => state.updatePending,
  logs: (state) => state.logs,
  requestIds: (state) => state.requestIds,
  logsPending: (state) => state.logsPending,
  queuePosition: (state) => state.queuePosition,
  queuePositionPending: (state) => state.queuePositionPending,
};

const mutations = {
  clearErrors() {
    // do nothing
  },
  beforeOnlinePresenceRequest(state) {
    state.pending = true;
  },
  afterOnlinePresenceRequest(state, data) {
    state.pending = false;
    state.onlinePresence = data;
  },
  beforeOnlinePresenceUpdateRequest(state) {
    state.updatePending = true;
  },
  afterOnlinePresenceUpdateRequest(state, { data, scriptId } = {}) {
    state.updatePending = false;
    Vue.set(state.requestIds, scriptId, data[scriptId]);
  },
  logs(state, data) {
    state.logsPending = false;
    state.logs = data;
  },
  beforeOnlinePresenceLogsRequest(state) {
    state.logsPending = true;
  },
  beforeQueuePositionRequest(state) {
    state.queuePositionPending = true;
  },
  queuePosition(state, { data, scriptId } = {}) {
    state.queuePositionPending = false;
    Vue.set(state.queuePosition, scriptId, data?.queuePosition);
  },
};

const actions = {
  async fetchOnline({ commit }, type) {
    commit('beforeOnlinePresenceRequest');
    try {
      const response = await ApiService.get(`${apiEndpoint}/property/online-presence/${type}`);
      commit('afterOnlinePresenceRequest', response.data?.data);
    } catch (error) {
      commit('afterOnlinePresenceRequest', []);
      throw handleApiError(commit, error);
    }
  },
  async updateOnlineData({ commit }, { type, scriptId } = {}) {
    try {
      commit('beforeOnlinePresenceUpdateRequest');
      const { data } = await ApiService.post(`${apiEndpoint}/property/online-presence/${type}/${scriptId}`);
      commit('afterOnlinePresenceUpdateRequest', { data, scriptId });
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
  async deleteOnlinePresenceData({ commit }) {
    try {
      commit('beforeOnlinePresenceUpdateRequest');
      await ApiService.delete(`${apiEndpoint}/property/online-presence`);
      commit('afterOnlinePresenceRequest', []);
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
  async fetchLogs({ commit }, requestId) {
    commit('beforeOnlinePresenceLogsRequest');
    try {
      const response = await ApiService.get(`${apiEndpoint}/property/online-presence/logs/${requestId}`);
      commit('logs', response.data?.data);
    } catch (error) {
      commit('afterOnlinePresenceRequest', []);
      throw handleApiError(commit, error);
    }
  },
  async getQueuePosition({ commit }, { scriptId, requestId } = {}) {
    commit('beforeQueuePositionRequest');
    try {
      const { data } = await ApiService.get(`${apiEndpoint}/property/online-presence/queue-position/${requestId}`);
      commit('queuePosition', { data, scriptId });
    } catch (error) {
      commit('afterOnlinePresenceRequest', []);
      throw handleApiError(commit, error);
    }
  },
  async updateWebsiteUrlReachableStatus({ commit }) {
    try {
      const response = await ApiService.post(`${apiEndpoint}/property/online-presence/update-website-url-reachable`);
      commit('afterOnlinePresenceRequest', response.data?.data);
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
